
import { format } from "date-fns";
import { CheckCircle2, Clock } from "lucide-react";
import { formatTime } from "../utils/payment-utils";
import { GuestList } from "./GuestList";
import { GroupedPayments } from "../types/payment-status";

interface TimeSlotCardProps {
  timeSlotKey: string;
  timeSlotData: GroupedPayments[string];
}

export const TimeSlotCard = ({ timeSlotKey, timeSlotData }: TimeSlotCardProps) => {
  // Calculate total booked including reserved slots, ensuring we have numbers
  const bookedPlayers = Number(timeSlotData.booked_players) || 0;
  const reservedSlots = Number(timeSlotData.reserved_slots) || 0;
  const paidPlayers = Number(timeSlotData.paid_players) || 0;
  const reservedPaidSlots = Number(timeSlotData.reserved_paid_slots) || 0;
  
  const totalBooked = bookedPlayers + reservedSlots;
  const totalPaid = paidPlayers + reservedPaidSlots;

  // Helper function to safely count players including guests
  const countPlayersWithGuests = (payments: any[]) => {
    return payments.reduce((total, payment) => {
      // Check if payment and booking exist before accessing guests
      if (payment && payment.booking) {
        return total + 1 + (payment.booking.guests?.length || 0);
      }
      return total;
    }, 0);
  };

  return (
    <div key={timeSlotKey} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">
          {format(new Date(timeSlotData.date), "EEEE, MMMM d, yyyy")}
          {" at "}
          {formatTime(timeSlotData.time)}
        </h3>
        <p className="text-sm">
          <span style={{ color: "#4d7fee" }}>
            Booked: {totalBooked}/{timeSlotData.max_players}
          </span>
          {" • "}
          <span style={{ color: "#34aa58" }}>
            Paid: {totalPaid}/{timeSlotData.max_players}
          </span>
        </p>
      </div>

      {timeSlotData.paid.length > 0 && (
        <div className="mt-4 mb-4">
          <h4 className="text-green-600 font-medium mb-2">
            <span className="flex items-center">
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Paid{" "}
              <span className="text-sm ml-2">
                ({countPlayersWithGuests(timeSlotData.paid)} players)
              </span>
            </span>
          </h4>
          <ul className="space-y-3 pl-6">
            {timeSlotData.paid.filter(payment => payment && payment.player).map((payment) => (
              <GuestList key={payment.id} payment={payment} />
            ))}
          </ul>
        </div>
      )}

      {timeSlotData.pending.length > 0 && (
        <div>
          <h4 className="text-orange-600 font-medium mb-2">
            <span className="flex items-center">
              <Clock className="w-4 h-4 mr-2" />
              Pending Payment{" "}
              <span className="text-sm ml-2">
                ({countPlayersWithGuests(timeSlotData.pending)} players)
              </span>
            </span>
          </h4>
          <ul className="space-y-3 pl-6">
            {timeSlotData.pending.filter(payment => payment && payment.player).map((payment) => (
              <GuestList key={payment.id} payment={payment} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
